/**
 * Serviço para gerenciar favoritos de tabs (páginas favoritas)
 */
import { apiService } from '../features/shared/service/api';

export interface UserFavoriteTab {
  id: string;
  user_id: string;
  company_id: string;
  path: string;
  label: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface UserFavoriteTabCreate {
  path: string;
  label: string;
  order_index?: number;
}

export interface UserFavoriteTabUpdate {
  label?: string;
  order_index?: number;
}

export interface UserFavoriteTabListResponse {
  favorites: UserFavoriteTab[];
  total: number;
}

class FavoriteTabsService {
  /**
   * Listar todos os favoritos do usuário atual
   */
  async getFavorites(): Promise<UserFavoriteTab[]> {
    try {
      const response = await apiService.getFavoriteTabs();
      return response.favorites;
    } catch (error) {
      console.error('Erro ao buscar favoritos:', error);
      throw error;
    }
  }

  /**
   * Criar um novo favorito
   */
  async createFavorite(favorite: UserFavoriteTabCreate): Promise<UserFavoriteTab> {
    try {
      const response = await apiService.createFavoriteTab(favorite);
      return response;
    } catch (error: any) {
      console.error('Erro ao criar favorito:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('já está nos seus favoritos')) {
        // Se já existe, buscar o favorito existente
        const favorites = await this.getFavorites();
        const existing = favorites.find(f => f.path === favorite.path);
        if (existing) {
          return existing;
        }
      }
      throw error;
    }
  }

  /**
   * Atualizar um favorito
   */
  async updateFavorite(favoriteId: string, update: UserFavoriteTabUpdate): Promise<UserFavoriteTab> {
    try {
      const response = await apiService.updateFavoriteTab(favoriteId, update);
      return response;
    } catch (error) {
      console.error('Erro ao atualizar favorito:', error);
      throw error;
    }
  }

  /**
   * Deletar um favorito
   */
  async deleteFavorite(favoriteId: string): Promise<void> {
    try {
      await apiService.deleteFavoriteTab(favoriteId);
    } catch (error) {
      console.error('Erro ao deletar favorito:', error);
      throw error;
    }
  }

  /**
   * Reordenar favoritos
   */
  async reorderFavorites(favoriteIds: string[]): Promise<UserFavoriteTab[]> {
    try {
      const response = await apiService.reorderFavoriteTabs(favoriteIds);
      return response.favorites;
    } catch (error) {
      console.error('Erro ao reordenar favoritos:', error);
      throw error;
    }
  }

  /**
   * Adicionar ou remover favorito baseado no path
   */
  async toggleFavorite(path: string, label: string): Promise<{ isFavorite: boolean; favorite?: UserFavoriteTab }> {
    try {
      const favorites = await this.getFavorites();
      const existing = favorites.find(f => f.path === path);

      if (existing) {
        // Remover favorito
        await this.deleteFavorite(existing.id);
        return { isFavorite: false };
      } else {
        // Adicionar favorito
        const newFavorite = await this.createFavorite({ path, label });
        return { isFavorite: true, favorite: newFavorite };
      }
    } catch (error) {
      console.error('Erro ao alternar favorito:', error);
      throw error;
    }
  }
}

export const favoriteTabsService = new FavoriteTabsService();

